@if (open()) {
  <section
    tabindex="-1"
    id="hub-assistant"
    class="assistant"
    [attr.inert]="pageAvailability.sessionPaused() ? '' : null"
    [class.assistant--session-paused]="pageAvailability.sessionPaused()"
    [class.assistant--maximized]="maximized()"
    aria-labelledby="assistant-title"
    (keydown.escape)="minimize(); $event.stopPropagation()"
  >
    <header class="assistant__header">
      <div class="assistant__brand">
        <app-icon name="hub-mark" [size]="24" />
        <div>
          <h2 id="assistant-title">Hub Assistant</h2>
          <span>{{ support.phase() === 'connected' ? (support.agentName() || 'Live agent') : 'Hub Central' }}</span>
        </div>
      </div>
      <div class="assistant__actions">
        @if (support.enabled && support.active() && support.phase() !== 'starting' && support.phase() !== 'waiting') {
          <button class="assistant__new-chat" type="button" [disabled]="support.phase() === 'ending'" (click)="support.end()">
            {{ support.cancelRequested() ? 'Cancel request' : 'End chat' }}
          </button>
        }
        <button class="assistant__new-chat assistant__new-chat--outlined" type="button" [disabled]="support.active()" (click)="reset()"><app-icon name="edit" [size]="16" /><span>New chat</span></button>
        <button
          type="button"
          (click)="minimize()"
          aria-label="Minimize assistant"
          title="Minimize assistant"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M5 12h14" />
          </svg>
        </button>
        <button
          type="button"
          (click)="maximized.set(!maximized())"
          [attr.aria-label]="maximized() ? 'Restore assistant' : 'Maximize assistant'"
          [title]="maximized() ? 'Restore' : 'Maximize'"
        >
          <app-icon [name]="maximized() ? 'copy' : 'expand'" [size]="18" />
        </button>
        <button
          type="button"
          (click)="close()"
          aria-label="Close assistant"
          title="Close assistant"
        >
          <app-icon name="close" [size]="18" />
        </button>
      </div>
    </header>
    <div #conversation class="assistant__conversation" (scroll)="trackConversationScroll()"
      tabindex="0" aria-label="Conversation history"
      (wheel)="onConversationWheel($event)" (keydown)="onConversationKey($event)"
      (touchstart)="onConversationTouch($event)" (touchmove)="onConversationTouch($event)">
      @if (messages().length === 0 && support.phase() === 'idle' && support.messages().length === 0) {
        <div class="assistant__welcome">
          <div class="assistant__emblem"><app-icon name="hub-mark" [size]="32" /></div>
          <h3>How can I help?</h3>
          <p class="assistant__intro">
            Ask about Hub Central, or start here.
          </p>
          <div class="assistant__suggestions">
            @for (suggestion of suggestions; track suggestion.label) {
              <button
                class="assistant__suggestion"
                type="button"
                (click)="draft = suggestion.question; composer.focus()"
              >
                <span class="assistant__card-icon"
                  ><app-icon [name]="suggestion.icon" [size]="20"
                /></span>
                <span class="assistant__card-copy"
                  ><strong>{{ suggestion.label }}</strong
                  ><span>{{ suggestion.description }}</span></span
                >
              </button>
            }
          </div>
        </div>
      }
      <div
        class="assistant__messages"
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        aria-relevant="additions"
      >
        @for (message of messages(); track message.id) {
          @if (message.role === 'system') {
            <div class="assistant-support-event"><span>{{ message.text }}</span></div>
          } @else {
          <article
            class="assistant__message"
            [class.assistant__message--user]="message.role === 'user'"
          >
            <header class="assistant__message-header">
              <span class="assistant__avatar"
                ><app-icon [name]="message.role === 'user' ? 'person' : 'hub-mark'" [size]="18"
              /></span>
              <span class="assistant__speaker">{{
                message.speaker || (message.role === 'user' ? 'You' : message.role === 'agent' ? 'Live agent' : 'Hub Assistant')
              }}</span>
            </header>
            <div class="assistant__message-body">
              @if (
                message.role === 'assistant' &&
                message.status === 'pending' &&
                !displayText(message).trim()
              ) {
                <p class="assistant__loading">
                  <span class="assistant__spinner" aria-hidden="true"></span>
                  <span>Preparing your answer…</span>
                </p>
              } @else {
                <p [textContent]="displayText(message)"></p>
              }
              @if (message.role === 'assistant' && message.status === 'complete') {
                <div class="assistant__navigation">
                  @for (link of screenLinks(message); track link.path) {
                    <a [routerLink]="link.path" (click)="minimize()"><app-icon name="requests" [size]="18" /><span>Open {{ link.label }}</span><app-icon name="arrow-right" [size]="18" /></a>
                  }
                  @if (showSupport(message)) {
                    @if (supportHref) {
                      <a [href]="supportHref"><app-icon name="info" [size]="18" /><span>Email Hub Support</span><app-icon name="arrow-right" [size]="18" /></a>
                    } @else {
                      <span>Use your approved Hub Support process.</span>
                    }
                  }
                </div>
              }
              @if (message.responseStatus === 'not_found') {
                <span class="assistant__response-status">No approved answer found</span>
              } @else if (message.responseStatus === 'refused') {
                <span class="assistant__response-status">Unable to help with this request</span>
              }
              @if (message.incomplete) {
                <p class="assistant__response-status" role="status">This answer reached its length limit and may be incomplete. Ask a narrower question for more detail.</p>
              }
              @if (message.role === 'assistant' && message.status === 'complete' && message.answerId) {
                <div class="assistant__feedback" role="group" aria-label="Response feedback" [attr.aria-busy]="message.feedbackBusy ? 'true' : null">
                  <button type="button" [disabled]="message.feedbackBusy || !message.feedback || message.feedbackError"
                    [attr.aria-pressed]="message.feedback?.vote === 'up'" aria-label="Helpful"
                    (click)="conversationState.saveFeedback(message.id, message.feedback?.vote === 'up' ? null : 'up')">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M7 10v11H3V10h4Zm0 0 5-8a3 3 0 0 1 3 3v4h4a2 2 0 0 1 2 2.4l-1.4 7A3 3 0 0 1 16.7 21H7"
                      />
                    </svg>
                  </button>
                  <button type="button" [disabled]="message.feedbackBusy || !message.feedback || message.feedbackError"
                    [attr.aria-pressed]="message.feedback?.vote === 'down'" aria-label="Not helpful"
                    (click)="conversationState.saveFeedback(message.id, message.feedback?.vote === 'down' ? null : 'down')">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        transform="rotate(180 12 12)"
                        d="M7 10v11H3V10h4Zm0 0 5-8a3 3 0 0 1 3 3v4h4a2 2 0 0 1 2 2.4l-1.4 7A3 3 0 0 1 16.7 21H7"
                      />
                    </svg>
                  </button>
                </div>
                @if (message.feedbackError) {
                  <p role="status">Feedback could not be confirmed. Your chat is unaffected.
                    <button type="button" (click)="conversationState.loadFeedback(message.id)">Reload feedback</button>
                  </p>
                }
                @if (message.feedback?.vote === 'down') {
                  <div class="assistant__feedback-comment">
                    <label [for]="'feedback-' + message.id">What could be better? (optional)</label>
                    <textarea [id]="'feedback-' + message.id" maxlength="1000" rows="2"
                      [disabled]="!!message.feedbackBusy || !!message.feedbackError"
                      [ngModel]="message.feedbackDraft ?? ''" [ngModelOptions]="{standalone: true}"
                      (ngModelChange)="conversationState.setFeedbackDraft(message.id, $event)"></textarea>
                    <button type="button" [disabled]="message.feedbackBusy || message.feedbackError || (message.feedbackDraft ?? '') === message.feedback.comment"
                      (click)="conversationState.saveFeedbackComment(message.id)">Save</button>
                  </div>
                }
                @if (message.feedbackConfirmation) {
                  <p class="assistant__feedback-confirmation" role="status">
                    <app-icon name="check" [size]="16" />
                    <span>Comment saved</span>
                  </p>
                }
              }
            </div>
          </article>
          }
        }
        @if (support.enabled && support.phase() !== 'idle' && support.phase() !== 'connected' && support.phase() !== 'ended') {
          <div class="assistant-support-state" role="status">
            <div class="assistant-support-event">
              @if (support.phase() === 'starting' || support.phase() === 'waiting' || support.phase() === 'reconnecting') {
                <span class="assistant__spinner" aria-hidden="true"></span>
              }
              <span>{{ support.status() }}</span>
            </div>
            @if (support.phase() === 'starting' || support.phase() === 'waiting') {
              <button class="assistant-support-inline" type="button" (click)="support.end()">Cancel request</button>
            }
          </div>
        }
        @if (support.notice()) { <p class="assistant-support-notice" role="status">{{ support.notice() }}</p> }
        @if (support.enabled && support.phase() === 'ended') {
          <div class="assistant-support-state">
            <button class="assistant-support-link" type="button" (click)="support.returnToAssistant()">Return to assistant</button>
          </div>
        }
      </div>
    </div>
    <form class="assistant__composer" (ngSubmit)="send()">
      <label class="assistant__input-label" for="assistant-question">{{ support.phase() === 'idle' ? 'Your question' : 'Your message' }}</label>
      <div class="assistant__input-box">
        <textarea
          #composer
          id="assistant-question"
          name="question"
          [(ngModel)]="draft"
          [maxlength]="maxQuestionLength"
          [readOnly]="pageAvailability.sessionPaused() || voice.active() || (support.phase() !== 'idle' && !support.canSend())"
          rows="2"
          [placeholder]="support.phase() === 'idle' ? 'Ask about Hub Central…' : support.phase() === 'ended' ? 'Return to assistant to continue' : 'Message your agent…'"
          (keydown)="onKeydown($event)"
        ></textarea>
        @if (voice.enabled && support.phase() === 'idle') {
          <button
            class="assistant__microphone"
            type="button"
            (click)="toggleVoiceConversation()"
            [disabled]="pageAvailability.sessionPaused() || !voice.conversationSupported || (!voice.active() && pending())"
            [attr.aria-pressed]="voice.active()"
            [attr.aria-label]="
              voice.active() ? 'End voice conversation' : 'Start voice conversation'
            "
            [title]="
              !voice.conversationSupported
                ? 'Voice conversation is not supported in this browser'
                : voice.active()
                  ? 'End voice conversation'
                  : 'Start voice conversation'
            "
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
              focusable="false"
            >
              @if (voice.active()) {
                <rect x="6" y="6" width="12" height="12" rx="2" />
              } @else {
                <path d="M4 10v4M8 6v12M12 3v18M16 6v12M20 10v4" />
              }
            </svg>
          </button>
        }
        <button
          class="assistant__send"
          type="submit"
          [disabled]="pageAvailability.sessionPaused() || !draft.trim() || pending() || voice.active() || (support.phase() !== 'idle' && !support.canSend())"
          aria-label="Send question"
          title="Send question"
        >
          <app-icon name="arrow-up" [size]="20" />
        </button>
      </div>
      @if (voice.enabled && support.phase() === 'idle') {
        <p class="assistant__voice-status" role="status">
          {{
            voice.active()
              ? voice.phase() === 'listening'
                ? 'Listening… Pause to send.'
                : voice.phase() === 'speaking'
                  ? 'Speaking…'
                  : 'Preparing your answer…'
              : voice.notice()
          }}
        </p>
        @if (voice.interim()) {
          <p class="assistant__voice-preview">{{ voice.interim() }}</p>
        }
      }
      <div class="assistant__footer">
        <p class="assistant__privacy">Do not enter credentials, private keys, or sensitive information.</p>
        <small>{{ draft.length }}/{{ maxQuestionLength }}</small>
      </div>
    </form>
    @if (support.enabled && support.phase() === 'idle') {
      <footer class="assistant-support-footer">
          <button class="assistant-support-link" type="button" [disabled]="pending()" (click)="startSupport()"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v7h4v-7H4Zm16 0h1v7h-4v-7h3Zm0 7c0 2-2 3-5 3h-2" /></svg><span>Talk to an agent</span></button>
      </footer>
    }
    <dialog
      #endDialog
      tabindex="-1"
      class="assistant__end-dialog"
      aria-labelledby="assistant-end-title"
      aria-describedby="assistant-end-description"
      (cancel)="$event.preventDefault(); keepChatting()"
      (keydown.escape)="$event.stopPropagation()"
    >
      <h2 id="assistant-end-title">End this chat?</h2>
      <p id="assistant-end-description">Your current conversation and draft will be cleared. Any live support chat will be ended.</p>
      <div class="assistant__end-actions">
        <button type="button" (click)="keepChatting()">Keep chatting</button>
        <button type="button" class="assistant__end-confirm" (click)="endChat()">End chat</button>
      </div>
    </dialog>
  </section>
}

<div #launcherArea class="assistant-launcher-area"
     [class.assistant-launcher-area--docked]="dockArea()"
     [style.left.px]="dockArea() ? dockLeft() : null">
@if (introductionVisible() && !open() && !pageAvailability.blocked()) {
  <aside #introductionBubble class="assistant-introduction" aria-label="Meet Hub Assistant"
         [style.max-width.px]="dockArea() ? introductionWidth() : null">
    <button
      class="assistant-introduction__open"
      type="button"
      (click)="toggle()"
      aria-controls="hub-assistant"
    >
      <app-icon name="hub-mark" [size]="20" />
      <span>Need help with Hub Central?</span>
    </button>
    <button
      type="button"
      (click)="dismissIntroduction()"
      aria-label="Dismiss assistant introduction"
    >
      <app-icon name="close" [size]="18" />
    </button>
  </aside>
}

@if (authenticated()) {
  <button
    #launcher
    appMovableAssistant
    [movementBounds]="dockArea() ? launcherBounds : null"
    class="assistant-launcher"
    [disabled]="pageAvailability.blocked()"
    type="button"
    (click)="toggle()"
    (pointerdown)="dismissIntroduction()"
    (keydown)="dismissIntroduction()"
    [attr.aria-expanded]="open()"
    aria-controls="hub-assistant"
    [attr.aria-label]="open() ? 'Minimize assistant' : 'Hub Assistant'"
    [attr.aria-description]="dockArea() ? 'Drag left or right within the assistant area. Alt plus left or right arrow moves the button; Home resets its position.' : 'Drag to move. Alt plus arrow keys moves the button; Home resets its position.'"
    [title]="dockArea() ? 'Hub Assistant - drag left or right' : 'Hub Assistant - drag to move'"
  >
    <app-icon name="hub-mark" [size]="26" />
  </button>
}
</div>
